import React from 'react';
import {
  Alert,
  Box,
  Button,
  ExpandableSection,
  Link,
  SpaceBetween,
} from '@amzn/awsui-components-console';

const DATA_CENTRAL_REQUEST_URL =
  'https://datacentral.a2z.com/datasets/access-request/create';
const DATA_CENTRAL_TRACK_URL =
  'https://datacentral.a2z.com/access-requests?tab=my-access-requests';
const ADDAPT_IAM_ROLE_FALLBACK =
  'arn:aws:iam::<ACCOUNT_ID>:role/FinTechADAADDAPT-LambdaExecutionRole-<STAGE>';

/**
 * Resolve the ADDAPT IAM role ARN from runtime settings.
 * Falls back to a placeholder template if settings are unavailable.
 */
function getAddaptIamRole(): string {
  const settings = (window as any).__ADDAPT_SETTINGS__;
  if (settings?.addaptIamRole) {
    return settings.addaptIamRole;
  }
  // Build from stage + metadata when available
  const stage = settings?.stage;
  const account = settings?.metadata?.account;
  if (stage && account) {
    return `arn:aws:iam::${account}:role/FinTechADAADDAPT-LambdaExecutionRole-${stage}`;
  }
  return ADDAPT_IAM_ROLE_FALLBACK;
}

export interface AccessRequestGuideProps {
  /** Table names that were denied or have role_no_access */
  deniedTables: string[];
}

/**
 * AccessRequestGuide — Shown in chat when access check finds denied tables.
 * Provides a direct link to Data Central and step-by-step instructions.
 */
export const AccessRequestGuide: React.FC<AccessRequestGuideProps> = ({
  deniedTables,
}) => {
  if (!deniedTables || deniedTables.length === 0) return null;

  return (
    <SpaceBetween size="m">
      <Alert type="warning" header="Access Required">
        <SpaceBetween size="xs">
          <Box>
            You don't have access to the following table(s):
          </Box>
          <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
            {deniedTables.map((table) => (
              <li key={table}>
                <code>{table}</code>
              </li>
            ))}
          </ul>
        </SpaceBetween>
      </Alert>

      <Box>
        <Button
          variant="primary"
          iconAlign="right"
          iconName="external"
          href={DATA_CENTRAL_REQUEST_URL}
          target="_blank"
        >
          Request Access on Data Central
        </Button>
      </Box>

      <ExpandableSection headerText="Step-by-step instructions" variant="footer">
        <SpaceBetween size="s">
          <Box variant="h4">On the Data Central Request Access page:</Box>

          <Box>
            <Box variant="strong">Step 1: Datasets to Access</Box>
            <Box variant="p" color="text-body-secondary">
              Click "Add Dataset" and search for the denied table(s) listed above.
              Select each one you need access to.
            </Box>
          </Box>

          <Box>
            <Box variant="strong">Step 2: Identities to Access</Box>
            <Box variant="p" color="text-body-secondary">
              Click "Add Identity" twice to add both identities:
            </Box>
            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
              <li>
                Identity Type = <strong>Team</strong>, Identity Value ={' '}
                <code>Fintech ADDAPT</code>
              </li>
              <li>
                Identity Type = <strong>IAM Role</strong>, Identity Value ={' '}
                <code>{getAddaptIamRole()}</code>
              </li>
            </ul>
          </Box>

          <Box>
            <Box variant="strong">Step 3: Submit</Box>
            <Box variant="p" color="text-body-secondary">
              Add a business justification explaining why you need access, then
              click "Submit Request".
            </Box>
          </Box>

          <Box>
            <Box variant="strong">Step 4: Track your request</Box>
            <Box variant="p" color="text-body-secondary">
              Monitor your request status at{' '}
              <Link href={DATA_CENTRAL_TRACK_URL} external>
                My Access Requests
              </Link>
            </Box>
          </Box>

          <Box>
            <Box variant="strong">Step 5: Wait for approval</Box>
            <Box variant="p" color="text-body-secondary">
              Wait for both requests (Team + IAM Role) to show "Approved" before
              retrying your operation in ADDAPT.
            </Box>
          </Box>
        </SpaceBetween>
      </ExpandableSection>
    </SpaceBetween>
  );
};

export default AccessRequestGuide;
